import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Checkbox, Space, Row, Card, Divider, Menu, Dropdown } from 'antd';

import DatasetBulkLoadEntityModal from '../entity/shared/EntityDropdown/DatasetBulkLoadEntityModal';

import { ReactComponent as DownloadOutlined } from '../../images/downloaded-outline.svg';
import { ReactComponent as DeleteOutlined } from '../../images/delete-outline.svg';
import { ReactComponent as UploadFile } from '../../images/upload-file.svg';
import { ReactComponent as MoreAction } from '../../images/more-vert.svg';
import { EntityXlsxExpectedSheetHeader } from '../entity/shared/types';
import { ComponentSpinner } from '../shared/ComponentSpinner';
import { useTranslation } from '../../i18n/useTranslation';

const GREY_COLOR = '#5F767C';
const DANGER_COLOR = '#d96758';

const styles = {
    row: { padding: '12px' },
    title: { margin: 0 },
};

interface BrowseResultActionsProps {
    // Props definition
    isAllSelected: boolean;
    isIndeterminate?: boolean;
    selectedItems: string[];
    isCsvExporting: boolean;
    exportCsv: () => void;
    handleSelectAll: (event) => void;
    confirmDatasetDeletion: () => void;
    expectedHeaders: EntityXlsxExpectedSheetHeader[];
    showCheckbox?: boolean;
}

export const BrowseDropDown = styled(Dropdown)`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

export const BrowseMenu = styled(Menu)`
    padding: 0px;
    border-radius: 4px;
    min-width: 140px !important;
`;

function BrowseResultActions({
    selectedItems,
    isAllSelected,
    isIndeterminate = false,
    isCsvExporting,
    handleSelectAll,
    confirmDatasetDeletion,
    exportCsv,
    expectedHeaders,
    showCheckbox = true,
}: BrowseResultActionsProps) {
    const { t } = useTranslation();
    const [isBulkLoadModalVisible, setIsBulkLoadModalVisible] = useState<boolean>(false);
    const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);

    const handleDropdownVisibleChange = (visible: boolean) => {
        setIsDropdownVisible(visible);
    };

    const menu = (
        <BrowseMenu
            id="actions-menu"
            role="menu"
            aria-label={t('Actions menu')}
            onKeyDown={(e) => {
                // Handle Escape key to close dropdown
                if (e.key === 'Escape') {
                    setIsDropdownVisible(false);
                }
            }}
        >
            <Menu.Item
                key="1"
                role="menuitem"
                tabIndex={0}
                onClick={
                    selectedItems.length === 0
                        ? undefined
                        : () => {
                              exportCsv();
                              setIsDropdownVisible(false);
                          }
                }
                className="f-color-gray-s100"
                disabled={selectedItems.length === 0}
                icon={<DownloadOutlined style={{ width: 16 }} color="#5F767C" />}
                aria-disabled={selectedItems.length === 0}
            >
                {t('Export')}
            </Menu.Item>
            <Divider className="ma-0" />
            <Menu.Item
                key="2"
                role="menuitem"
                tabIndex={0}
                className="f-color-risk-very-high"
                disabled={selectedItems.length === 0}
                icon={<DeleteOutlined color={DANGER_COLOR} style={{ width: 10 }} />}
                onClick={
                    selectedItems.length === 0
                        ? undefined
                        : () => {
                              confirmDatasetDeletion();
                              setIsDropdownVisible(false);
                          }
                }
                aria-disabled={selectedItems.length === 0}
            >
                {t('Delete Datasets')}
            </Menu.Item>
        </BrowseMenu>
    );

    return (
        <>
            {isCsvExporting && <ComponentSpinner />}
            <Divider className="ma-0" />
            <Card>
                <Row
                    style={styles.row}
                    justify={showCheckbox ? 'space-between' : 'end'}
                    align="middle"
                    className="f-px-4-xs"
                >
                    {showCheckbox && (
                        <Space size="middle" align="center">
                            <Checkbox
                                checked={isAllSelected}
                                indeterminate={isIndeterminate}
                                onChange={handleSelectAll}
                            >
                                {t('Select All')}
                            </Checkbox>
                        </Space>
                    )}
                    <Row align="middle">
                        <Button
                            type="link"
                            className="mr-3"
                            onClick={() => setIsBulkLoadModalVisible(true)}
                            icon={<UploadFile style={{ width: 12 }} color={GREY_COLOR} />}
                        >
                            <span className="f-color-gray-s100">{t('Bulk Import')}</span>
                        </Button>
                        <BrowseDropDown
                            overlay={menu}
                            trigger={['click']}
                            onVisibleChange={handleDropdownVisibleChange}
                            visible={isDropdownVisible}
                        >
                            <Button
                                type="text"
                                aria-expanded={isDropdownVisible}
                                aria-haspopup="true"
                                aria-controls="actions-menu"
                            >
                                <Row align="middle" style={{ gap: 8 }}>
                                    <MoreAction
                                        data-testid="entity-header-dropdown"
                                        width={12}
                                        height={12}
                                        color={GREY_COLOR}
                                    />
                                    <span className="f-color-gray-s100">{t('Actions')}</span>
                                </Row>
                            </Button>
                        </BrowseDropDown>
                    </Row>
                </Row>
                <Divider className="ma-0" />
            </Card>

            {/* BulkLoad Entity Modal */}
            {isBulkLoadModalVisible && (
                <DatasetBulkLoadEntityModal
                    onClose={() => setIsBulkLoadModalVisible(false)}
                    expectedHeadersFromApi={expectedHeaders}
                />
            )}
        </>
    );
}

export default BrowseResultActions;
